import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import '@/styles/RoundCounter/RoundCounter.less';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/assets/icons/LibrarySvg';
import RoundCounterForm from '@/components/RoundCounter/RoundCountersForm';
import RoundCounterToggleButton from '@/components/RoundCounter/ToggleFormButton';
import { loadFromLocalStorage, removeFromLocalStorage, saveToLocalStorage, } from '@/utils/localStorage';
const RoundCounter = ({ currentCharacterIdx, combatMapId, currentRoundNumber = 1, countDownRounds = 0, className, isMainCounter = true, label, id, onRoundChange, onRoundsLeftChange, onCountDownComplete, onRoundReset, onCreateNewCounter, onCreateNewCountdown, pageButtonsContainer, showToggleButton = true, }) => {
    const { t } = useTranslation();
    const formRef = useRef(null);
    const currentCountdownInputRef = useRef(null);
    const addNewCounterInputLabelRef = useRef(null);
    const addNewCountdownInputLabelRef = useRef(null);
    const addNewCountdownInputNumberRef = useRef(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const prevPropIdxRef = useRef(null);
    const prevPropRoundNumberRef = useRef(currentRoundNumber);
    const prevPropCountDownRoundsRef = useRef(countDownRounds);
    const [prevCombatMapId, setPrevCombatMapId] = useState(combatMapId);
    const [roundNumber, setRoundNumber] = useState(currentRoundNumber);
    const [roundsLeft, setRoundsLeft] = useState(countDownRounds);
    const isAdditionalCountDown = roundsLeft > 0 && !isMainCounter;
    const isNewCombatMapId = prevCombatMapId !== combatMapId;
    const storageKey = useMemo(() => {
        const noCombatMapId = combatMapId === undefined || combatMapId === null;
        const noId = id === undefined || id === null;
        if (isMainCounter && !noCombatMapId) {
            return `roundCounter-main-${combatMapId}`;
        }
        else if (!noCombatMapId && !noId) {
            return `roundCounter-${combatMapId}-${id}`;
        }
        return null;
    }, [combatMapId, id, isMainCounter]);
    /** Отслеживание изменений combatMapId */
    useEffect(() => {
        if (combatMapId !== prevCombatMapId) {
            setPrevCombatMapId(combatMapId);
        }
    }, [combatMapId, prevCombatMapId]);
    /** Загрузка состояния при монтировании */
    useEffect(() => {
        if (!storageKey)
            return;
        const savedState = loadFromLocalStorage(storageKey);
        if (savedState) {
            if (savedState.roundNumber !== undefined)
                setRoundNumber(savedState.roundNumber);
            if (savedState.roundsLeft !== undefined)
                setRoundsLeft(savedState.roundsLeft);
        }
    }, [storageKey]);
    /** Сохранение состояния при изменении */
    useEffect(() => {
        if (!storageKey)
            return;
        const exists = !!loadFromLocalStorage(storageKey);
        if (isNewCombatMapId && !isMainCounter)
            return;
        if (!exists) {
            if (isMainCounter && isNewCombatMapId) {
                handleRoundReset();
            }
            if (!isMainCounter && isNewCombatMapId) {
                return;
            }
        }
        saveToLocalStorage(storageKey, { roundNumber, roundsLeft, label, counterIdx: id });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey, id, label, roundNumber, roundsLeft, isMainCounter, isNewCombatMapId]);
    /** Синхронизация состояния с пропсами */
    useEffect(() => {
        if (currentRoundNumber !== prevPropRoundNumberRef.current) {
            if (storageKey) {
                const savedState = loadFromLocalStorage(storageKey);
                if (!savedState || savedState.roundNumber === undefined) {
                    setRoundNumber(currentRoundNumber);
                }
            }
            else {
                setRoundNumber(currentRoundNumber);
            }
            prevPropRoundNumberRef.current = currentRoundNumber;
        }
    }, [currentRoundNumber, storageKey]);
    useEffect(() => {
        if (countDownRounds !== prevPropCountDownRoundsRef.current) {
            setRoundsLeft(countDownRounds);
            prevPropCountDownRoundsRef.current = countDownRounds;
        }
    }, [countDownRounds]);
    /** Обновление раунда счетчика */
    useEffect(() => {
        if (isNewCombatMapId)
            return;
        if (currentCharacterIdx < 0)
            return;
        if (prevPropIdxRef.current === currentCharacterIdx)
            return;
        if (currentCharacterIdx === 0 && prevPropIdxRef.current !== null) {
            setRoundNumber((r) => r + 1);
            onRoundChange === null || onRoundChange === void 0 ? void 0 : onRoundChange(roundNumber + 1);
            if (roundsLeft > 0) {
                setRoundsLeft((r) => r - 1);
                onRoundsLeftChange === null || onRoundsLeftChange === void 0 ? void 0 : onRoundsLeftChange(roundsLeft - 1);
                if (roundsLeft === 1) {
                    onCountDownComplete === null || onCountDownComplete === void 0 ? void 0 : onCountDownComplete();
                    if (!isMainCounter) {
                        if (!storageKey)
                            return;
                        removeFromLocalStorage(storageKey);
                    }
                }
            }
        }
        prevPropIdxRef.current = currentCharacterIdx;
    }, [
        currentCharacterIdx,
        storageKey,
        isMainCounter,
        onCountDownComplete,
        onRoundChange,
        onRoundsLeftChange,
        roundNumber,
        roundsLeft,
        isNewCombatMapId,
    ]);
    // ---------
    // ---------
    // ---------
    const handleRoundReset = () => {
        setRoundNumber(1);
        setRoundsLeft(0);
        onRoundReset === null || onRoundReset === void 0 ? void 0 : onRoundReset();
    };
    const toggleForm = () => {
        setIsFormOpen((v) => {
            if (!v) {
                if (currentCountdownInputRef.current)
                    currentCountdownInputRef.current.value = '';
                if (addNewCounterInputLabelRef.current)
                    addNewCounterInputLabelRef.current.value = '';
                if (addNewCountdownInputLabelRef.current)
                    addNewCountdownInputLabelRef.current.value = '';
                if (addNewCountdownInputNumberRef.current)
                    addNewCountdownInputNumberRef.current.value = '';
            }
            return !v;
        });
    };
    // Закрытие формы при клике вне её
    useEffect(() => {
        function onDown(e) {
            if (!isFormOpen || !formRef.current)
                return;
            if (!formRef.current.contains(e.target))
                setIsFormOpen(false);
        }
        document.addEventListener('mousedown', onDown);
        return () => {
            document.removeEventListener('mousedown', onDown);
        };
    }, [isFormOpen]);
    const handleAddMainCountdown = () => {
        var _a, _b;
        const raw = (_b = (_a = currentCountdownInputRef.current) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
        if (!raw)
            return;
        const val = Number(raw);
        if (val >= 0) {
            setRoundsLeft(val);
            onRoundsLeftChange === null || onRoundsLeftChange === void 0 ? void 0 : onRoundsLeftChange(val);
        }
    };
    const handleAddNewCounter = () => {
        var _a, _b;
        const label = ((_b = (_a = addNewCounterInputLabelRef.current) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.trim()) ||
            t('initiativeTracker.roundCounter.label');
        // добавляем новый счётчик
        onCreateNewCounter === null || onCreateNewCounter === void 0 ? void 0 : onCreateNewCounter(label);
        if (addNewCounterInputLabelRef.current) {
            addNewCounterInputLabelRef.current.value = '';
        }
    };
    const handleAddNewCountdown = () => {
        var _a, _b, _c, _d;
        let label = (_b = (_a = addNewCountdownInputLabelRef.current) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.trim();
        label = label || t('initiativeTracker.roundCounter.roundsLeft');
        const rounds = Number((_d = (_c = addNewCountdownInputNumberRef.current) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : 0);
        if (rounds <= 0)
            return;
        // добавляем новый обратный отсчёт
        onCreateNewCountdown === null || onCreateNewCountdown === void 0 ? void 0 : onCreateNewCountdown(label, rounds);
        if (addNewCountdownInputLabelRef.current) {
            addNewCountdownInputLabelRef.current.value = '';
        }
        if (addNewCountdownInputNumberRef.current) {
            addNewCountdownInputNumberRef.current.value = '';
        }
    };
    return (_jsxs(_Fragment, { children: [isMainCounter && (_jsxs(_Fragment, { children: [pageButtonsContainer &&
                        (showToggleButton || isMainCounter) &&
                        createPortal(_jsx(RoundCounterToggleButton, { onClick: toggleForm, show: showToggleButton, pageButtonsContainer: pageButtonsContainer }), pageButtonsContainer), _jsx(RoundCounterForm, { isOpen: isFormOpen, formRef: formRef, roundsLeft: roundsLeft, currentCountdownInputRef: currentCountdownInputRef, addNewCounterInputLabelRef: addNewCounterInputLabelRef, addNewCountdownInputLabelRef: addNewCountdownInputLabelRef, addNewCountdownInputNumberRef: addNewCountdownInputNumberRef, handleAddMainCountdown: handleAddMainCountdown, handleRoundReset: handleRoundReset, handleAddNewCounter: handleAddNewCounter, handleAddNewCountdown: handleAddNewCountdown })] })), _jsxs("div", { className: `round-counter ${isMainCounter ? 'main' : 'additional'} ${className !== null && className !== void 0 ? className : ''}`, children: [isMainCounter && (_jsx("button", { className: "reset-btn", onClick: handleRoundReset, type: "button", title: "Reset round", children: _jsx(Icon, { name: "refresh_4" }) })), !isAdditionalCountDown && (
                    // Round counter
                    _jsxs("div", { className: "counter-container", children: [_jsx("span", { className: "label", children: label || t('initiativeTracker.roundCounter.label') }), _jsx("span", { className: "number", children: roundNumber })] })), roundsLeft > 0 && (
                    // Round countdown
                    _jsxs("div", { className: `countdown-container ${roundsLeft === 1 ? 'last-round' : ''}`, children: [_jsxs("div", { className: "countdown", children: [_jsx("span", { className: "label", children: isAdditionalCountDown ? label : t('initiativeTracker.roundCounter.roundsLeft') }), _jsx("span", { className: "number", children: roundsLeft })] }), roundsLeft === 1 && (
                            // Last round alert
                            _jsx("div", { className: "last-round-alert", children: _jsx("span", { children: t('initiativeTracker.roundCounter.lastRound') }) }))] }))] })] }));
};
export default memo(RoundCounter);
